// ============================================
// B356 Fase 1 (AI_154) — RECEITA DE CONTROLE ESPECIAL ELETRÔNICA
// ============================================
// Reprodução do MODELO OFICIAL da Anvisa ("receita-de-controle-especial-
// eletronica.pdf", em docs/legislacao/sncr/). A página "Receituário Eletrônico"
// do SNCR é explícita: "Não é permitida a customização dos modelos [...]
// devendo ser observados integralmente os modelos disponibilizados pela Anvisa."
// Por isso este documento NÃO tem logo, cabeçalho do ambulatório nem cores do
// app — só os campos do modelo, na ordem do modelo.
//
// Regras que se aplicam (RDC 1.000/2025):
//   - Art. 7º  número individualizado do SNCR ("N.º");
//   - Art. 8º  assinatura eletrônica QUALIFICADA (ICP-Brasil) — só é emitido
//              no fluxo de assinatura digital;
//   - Art. 10  eletrônica NÃO é emitida em 2 vias.
// Um medicamento por documento: o modelo tem um único bloco de PRESCRIÇÃO
// (decisão conservadora da AI_154, até a Anvisa esclarecer).
//
// AI_155 (B356-FU, padrão Memed): o documento assinado leva 1º uma folha
// PERSONALIZADA (receituário do ambulatório, com logo — fica com o paciente) e
// depois esta folha OFICIAL, intacta. Por isso o modelo virou um FRAGMENTO
// (`montarFolhaOficialSncr`: estilos escopados + página nomeada, como o LME),
// anexado ao receituário comum. A Receita sujeita à Retenção (RET) não tem
// modelo publicado pela Anvisa: segue o mesmo desenho do modelo da RCE (como a
// Memed faz), com os dados do paciente que a RDC 471/2021 exige (idade e sexo).

import { Paciente } from "../../../types";
import { escapeHtml } from "../../../shared/utils/escapeHtml";
import { calcularIdade } from "../../../utils/dates";

export interface DadosRceEletronica {
  numero: string;
  medicamento: { nome: string; posologia: string };
  emitente: {
    nome: string;
    /** ex.: "CRM 3793/RO" */
    inscricao: string;
    endereco: string;
    telefone: string;
  };
  /** "hepato.app — RAZÃO SOCIAL — CNPJ 00.000.000/0000-00" */
  identificacaoPlataforma: string;
  /** Nome, registro e data/hora da assinatura digital. */
  assinatura: { nome: string; registro: string; data: string };
  /** URL pública de verificação do documento (B290) e o QR dela. */
  urlVerificacao: string;
  blocoQR: string;
  /** Homologação: tarja visível — número de teste, sem valor legal. */
  homologacao?: boolean;
  /** Fichas anexas (LME etc.) — páginas SEPARADAS após a receita, que fica intacta. */
  anexos?: { estilos: string; corpo: string }[];
}

/**
 * "Sertralina 50mg comprimido" → { nome: "Sertralina", concentracao: "50mg",
 * forma: "comprimido" }. Associações ("Codeína + Paracetamol 30mg + 500mg
 * comprimido") mantêm as doses juntas. Sem dose reconhecível, tudo vai no nome.
 */
export function decomporMedicamento(nomeCompleto: string): {
  nome: string;
  concentracao: string;
  forma: string;
} {
  const texto = (nomeCompleto || "").split("\n")[0].trim();
  const re =
    /(\d+(?:[.,]\d+)?\s*(?:mg|mcg|µg|g|ui|%)(?:\s*\/\s*\d*(?:[.,]\d+)?\s*(?:ml|g|h|dose))?)(\s*\+\s*\d+(?:[.,]\d+)?\s*(?:mg|mcg|µg|g|ui|%)(?:\s*\/\s*\d*(?:[.,]\d+)?\s*(?:ml|g|h|dose))?)*/i;
  const m = re.exec(texto);
  if (!m || m.index === 0) return { nome: texto, concentracao: "", forma: "" };
  return {
    nome: texto.slice(0, m.index).trim(),
    concentracao: m[0].replace(/\s+/g, " ").trim(),
    forma: texto.slice(m.index + m[0].length).trim(),
  };
}

/** Posologia do app: 1ª linha = modo de uso; demais = quantidade/"Uso contínuo". */
export function separarPosologia(posologia: string): {
  posologia: string;
  quantidade: string;
} {
  const linhas = (posologia || "").split("\n").map((l) => l.trim()).filter(Boolean);
  return { posologia: linhas[0] || "", quantidade: linhas.slice(1).join(" ") };
}

export function enderecoCompletoPaciente(p: Paciente): string {
  const rua = [p.logradouro, p.numero].filter(Boolean).join(", ");
  const partes = [
    rua,
    p.complemento,
    p.bairro,
    [p.cidade, p.estado].filter(Boolean).join("/"),
    p.cep ? `CEP ${p.cep}` : "",
  ].filter((x) => x && String(x).trim());
  return partes.join(" — ");
}

/** "1980-12-13" ou "13/12/1980" → "13/12/1980 (45 anos)". */
export function nascimentoComIdade(dataNascimento: string | undefined): string {
  const d = String(dataNascimento || "").trim();
  if (!d) return "";
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(d);
  const br = iso ? `${iso[3]}/${iso[2]}/${iso[1]}` : d;
  let idade = "";
  try {
    const n = calcularIdade(d);
    if (Number.isFinite(n) && n >= 0) idade = ` (${n} anos)`;
  } catch {
    /* data em formato inesperado: mostra só a data */
  }
  return br + idade;
}

export type TipoModeloSncr = "RCE" | "RET";

export interface DadosModeloSncr extends Omit<DadosRceEletronica, "medicamento" | "anexos"> {
  tipo: TipoModeloSncr;
  /** RCE: um único medicamento (decisão AI_154). RET: todos os do documento. */
  medicamentos: { nome: string; posologia: string }[];
}

const TITULO_MODELO: Record<TipoModeloSncr, string> = {
  RCE: "RECEITA DE CONTROLE ESPECIAL",
  RET: "RECEITA SUJEITA À RETENÇÃO",
};

// Estilos ESCOPADOS em .folha-sncr: o fragmento entra no mesmo HTML do
// receituário e não pode alterar a página personalizada.
const ESTILOS_FOLHA_SNCR = `
  @page sncr { size: A4; margin: 14mm 14mm 12mm 14mm; }
  .folha-sncr { page: sncr; page-break-before: always; font-family: 'Open Sans', 'Helvetica Neue', Arial, sans-serif;
         color: #111; font-size: 11.5px; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .folha-sncr, .folha-sncr * { box-sizing: border-box; }
  .folha-sncr div, .folha-sncr h1, .folha-sncr span { margin: 0; padding: 0; }
  .folha-sncr .rce-topo { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 22px; }
  .folha-sncr .rce-titulo { text-align: center; flex: 1; }
  .folha-sncr .rce-titulo h1 { font-size: 19px; font-weight: 700; letter-spacing: 0.2px; }
  .folha-sncr .rce-titulo .rce-sub { font-size: 12px; font-weight: 700; text-align: right; margin-right: 40px; }
  .folha-sncr .rce-numero { border: 1px solid #111; min-width: 210px; padding: 6px 8px; font-size: 13px; }
  .folha-sncr .rce-numero b { font-size: 13.5px; letter-spacing: 0.3px; }
  .folha-sncr .rce-blocos { display: flex; gap: 16px; margin-bottom: 26px; }
  .folha-sncr .rce-caixa { border: 1px solid #111; flex: 1; }
  .folha-sncr .rce-cab { border-bottom: 1px solid #111; text-align: center; font-weight: 700; font-size: 12.5px; padding: 3px 0; }
  .folha-sncr .rce-caixa .rce-corpo { padding: 8px 8px 12px; }
  .folha-sncr .rce-campo { margin: 0 0 12px; line-height: 1.35; }
  .folha-sncr .rce-campo:last-child { margin-bottom: 0; }
  .folha-sncr .rce-rot { text-transform: uppercase; font-size: 10.5px; }
  .folha-sncr .rce-val { font-weight: 600; }
  .folha-sncr .rce-prescricao { border: 1px solid #111; min-height: 300px; margin-bottom: 22px; }
  .folha-sncr .rce-prescricao .rce-corpo { padding: 10px 10px 14px; }
  .folha-sncr .rce-item { margin-bottom: 12px; line-height: 1.4; }
  .folha-sncr .rce-item b { font-weight: 700; }
  .folha-sncr .rce-rodape { display: flex; justify-content: space-between; align-items: flex-end; gap: 16px;
            font-size: 8.4px; font-style: italic; }
  .folha-sncr .rce-rodape .rce-linhas { flex: 1; }
  .folha-sncr .rce-rodape .rce-linhas div { margin-bottom: 7px; }
  .folha-sncr .rce-rodape .rce-linhas b { font-style: normal; }
  .folha-sncr .rce-homolog { border: 2px solid #b91c1c; color: #b91c1c; font-weight: 700; text-align: center;
             font-size: 12px; padding: 4px; margin-bottom: 12px; }
`;

/** Folha OFICIAL (RCE ou RET) como fragmento p/ anexar ao receituário personalizado. */
export function montarFolhaOficialSncr(
  paciente: Paciente,
  d: DadosModeloSncr
): { estilos: string; corpo: string } {
  const e = escapeHtml;
  const campo = (rotulo: string, valor: string) =>
    `<div class="rce-campo"><span class="rce-rot">${rotulo}</span> <span class="rce-val">${e(valor)}</span></div>`;

  let paciente_html: string;
  let prescricao_html: string;
  if (d.tipo === "RCE") {
    const m = d.medicamentos[0] || { nome: "", posologia: "" };
    const med = decomporMedicamento(m.nome);
    const pos = separarPosologia(m.posologia);
    paciente_html = `
        ${campo("Nome:", paciente.nome)}
        ${campo("CPF ou, se estrangeiro, passaporte:", paciente.cpf || "")}
        ${campo("Endereço completo:", enderecoCompletoPaciente(paciente))}`;
    prescricao_html = `
      ${campo("Nome do medicamento ou substância:", med.nome)}
      ${campo("Concentração:", med.concentracao)}
      ${campo("Forma farmacêutica:", med.forma)}
      ${campo("Quantidade:", pos.quantidade)}
      ${campo("Posologia:", pos.posologia)}`;
  } else {
    const sexo = paciente.sexo === "M" ? "Masculino" : paciente.sexo === "F" ? "Feminino" : "";
    const tel = paciente.telefone1 || paciente.telefone || "";
    paciente_html = `
        ${campo("Nome:", paciente.nome)}
        ${campo("CPF:", paciente.cpf || "")}
        ${campo("Data de nascimento:", nascimentoComIdade(paciente.dataNascimento))}
        ${campo("Sexo:", sexo)}
        ${campo("Endereço:", enderecoCompletoPaciente(paciente))}
        ${tel ? campo("Telefone:", tel) : ""}`;
    prescricao_html = d.medicamentos
      .map((m) => {
        const pos = separarPosologia(m.posologia);
        const nome = (m.nome || "").split("\n")[0];
        return `<div class="rce-item"><b>${e(nome)}</b>${pos.quantidade ? ` — ${e(pos.quantidade)}` : ""}${
          pos.posologia ? `<br>${e(pos.posologia)}` : ""
        }</div>`;
      })
      .join("");
  }

  const corpo = `<div class="folha-sncr">
  ${
    d.homologacao
      ? `<div class="rce-homolog">HOMOLOGAÇÃO — NUMERAÇÃO DE TESTE DO SNCR, SEM VALOR LEGAL. NÃO DISPENSAR.</div>`
      : ""
  }
  <div class="rce-topo">
    <div class="rce-titulo">
      <h1>${TITULO_MODELO[d.tipo]}</h1>
      <div class="rce-sub">ELETRÔNICA</div>
    </div>
    <div class="rce-numero">N.º <b>${e(d.numero)}</b></div>
  </div>

  <div class="rce-blocos">
    <div class="rce-caixa">
      <div class="rce-cab">EMITENTE</div>
      <div class="rce-corpo">
        ${campo("Nome do prescritor:", d.emitente.nome)}
        ${campo("Inscrição no conselho profissional / UF:", d.emitente.inscricao)}
        ${campo("Endereço completo:", d.emitente.endereco)}
        ${campo("Telefone:", d.emitente.telefone)}
      </div>
    </div>
    <div class="rce-caixa">
      <div class="rce-cab">PACIENTE</div>
      <div class="rce-corpo">${paciente_html}
      </div>
    </div>
  </div>

  <div class="rce-prescricao">
    <div class="rce-cab">PRESCRIÇÃO</div>
    <div class="rce-corpo">${prescricao_html}
    </div>
  </div>

  <div class="rce-rodape">
    <div class="rce-linhas">
      <div>IDENTIFICAÇÃO DA PLATAFORMA: <b>${e(d.identificacaoPlataforma)}</b></div>
      <div>REGISTRO DA ASSINATURA DIGITAL E DATA: <b>Assinado digitalmente (ICP-Brasil) por ${e(
        d.assinatura.nome
      )} — ${e(d.assinatura.registro)} — ${e(d.assinatura.data)}</b></div>
      <div>VALIDE ESSA ASSINATURA EM <b>validar.iti.gov.br</b></div>
      <div>ESTA PRESCRIÇÃO PODE SER VERIFICADA EM: <b>${e(d.urlVerificacao)}</b></div>
      <div>VERIFIQUE A NUMERAÇÃO E REGISTRE A UTILIZAÇÃO DESTA PRESCRIÇÃO EM <b>sncr.anvisa.gov.br</b></div>
    </div>
    <div>${d.blocoQR}</div>
  </div>
</div>`;
  return { estilos: ESTILOS_FOLHA_SNCR, corpo };
}

/** Documento AUTÔNOMO só com a folha oficial da RCE (testes/inspeção). */
export function montarRceEletronicaHTML(paciente: Paciente, d: DadosRceEletronica): string {
  const { medicamento, anexos, ...resto } = d;
  const folha = montarFolhaOficialSncr(paciente, { ...resto, tipo: "RCE", medicamentos: [medicamento] });
  return `<!DOCTYPE html>
<html lang="pt-BR">
<head>
<meta charset="UTF-8">
<title>Receita de Controle Especial Eletrônica - ${escapeHtml(paciente.nome)}</title>
<style>
  body { margin: 0; }
  ${folha.estilos.replace("page-break-before: always;", "")}
  ${(anexos || []).map((a) => a.estilos).join("\n")}
</style>
</head>
<body>
  ${folha.corpo}
  ${(anexos || []).map((a) => a.corpo).join("")}
</body>
</html>`;
}
